// src/Redux/thunks/healthBotThunks.js
import { createAsyncThunk } from "@reduxjs/toolkit";
import { axiosHealthBot } from "../../../api/axios";
import ENDPOINTS from "../../../api/endpoints";
import { selectHBExpiresAt, selectHBToken, selectHBDlEndpoint, selectHBUserId } from "../selectors/healthBotSelectors";

/**
 * Get Direct Line token via Gateway:
 * GET /medical-assistant/token?userId=...
 */
export const hbGetToken = createAsyncThunk(
    "healthBot/getToken",
    async ({ userId }, { rejectWithValue }) => {
        try {
            const { data } = await axiosHealthBot.get(ENDPOINTS.HEALTH_BOT.TOKEN, {
                params: { userId },
            });

            const expiresAt =
                typeof data?.expiresAt === "number"
                    ? data.expiresAt
                    : Date.now() + ((data?.expires_in ?? 1800) * 1000) - 60_000; // 60s margin

            return {
                token: data?.token,
                expiresAt,
                directLineEndpoint: data?.directLineEndpoint ?? null,
                userId, // keep it so we can re-mint if needed
            };
        } catch (e) {
            return rejectWithValue(e?.response?.data || "token_error");
        }
    }
);

/**
 * Refresh Direct Line token via Gateway:
 * POST /medical-assistant/refresh { token, directLineEndpoint? }
 * - If DL rejects, the server re-issues a fresh token and returns { reissued: true }
 */
export const hbRefreshToken = createAsyncThunk(
    "healthBot/refreshToken",
    async (_payload, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            const token = selectHBToken(state);
            const directLineEndpoint = selectHBDlEndpoint(state);

            const { data } = await axiosHealthBot.post(ENDPOINTS.HEALTH_BOT.REFRESH, {
                token,
                directLineEndpoint, // helps avoid region mismatch
            });

            const expiresAt =
                typeof data?.expiresAt === "number"
                    ? data.expiresAt
                    : Date.now() + ((data?.expires_in ?? 1800) * 1000) - 60_000;

            return {
                token: data?.token,
                expiresAt,
                directLineEndpoint: data?.directLineEndpoint ?? directLineEndpoint ?? null,
                reissued: !!data?.reissued,
            };
        } catch (e) {
            return rejectWithValue(e?.response?.data || "refresh_error");
        }
    }
);

/**
 * Convenience helper:
 * - If no token: mint one (needs userId).
 * - If expiring in < 60s: refresh.
 * - Otherwise: no-op.
 */
export const hbEnsureToken = createAsyncThunk(
    "healthBot/ensureToken",
    async ({ userId }, { getState, dispatch }) => {
        const state = getState();
        const token = selectHBToken(state);
        const expiresAt = selectHBExpiresAt(state);

        if (!token) {
            const res = await dispatch(hbGetToken({ userId }));
            return res.payload;
        }
        if (Date.now() > (expiresAt ?? 0) - 60_000) {
            const res = await dispatch(hbRefreshToken());
            // If refresh failed, try a fresh token using known userId from state or arg.
            if (!res.meta.requestStatus || res.meta.requestStatus === "rejected") {
                const fallbackUserId = selectHBUserId(getState()) ?? userId ?? "guest";
                const again = await dispatch(hbGetToken({ userId: fallbackUserId }));
                return again.payload;
            }
            return res.payload;
        }
        // still valid -> return current snapshot
        return { token, expiresAt };
    }
);